import type { LocalPresetRecord, ServerPresetRecord } from "../../shared/types.js";
import { isPendingLibraryDelete } from "./delete-library-preset.js";
import { getLocalPreset, listLocalPresets, saveLocalPreset, deleteLocalPreset } from "./idb.js";

/** True when local and remote identify the same compiled version for cache reuse. */
export function samePresetVersion(
  local: Pick<LocalPresetRecord, "versionId" | "contentHash"> | null | undefined,
  remote: Pick<ServerPresetRecord, "versionId" | "contentHash">,
): boolean {
  if (!local) return false;
  if (remote.versionId && local.versionId) {
    return local.versionId === remote.versionId;
  }
  if (remote.contentHash && local.contentHash) {
    return local.contentHash === remote.contentHash;
  }
  // Old local records without version/content hash are unverified.
  return false;
}

/** Fetch server presets and merge into IndexedDB with version-aware preview reuse. */
export async function syncLibraryFromServer(): Promise<LocalPresetRecord[]> {
  const res = await fetch("/api/presets", { credentials: "include" });
  if (!res.ok) {
    return listLocalPresets();
  }

  const data = (await res.json()) as { presets: ServerPresetRecord[] };
  const remoteIds = new Set(data.presets.map((remote) => remote.id));
  for (const remote of data.presets) {
    if (!remote.model || isPendingLibraryDelete(remote.id)) continue;
    const existing = await getLocalPreset(remote.id);
    const sameVersion = samePresetVersion(existing, remote);
    const record: LocalPresetRecord = {
      id: remote.id,
      name: remote.name,
      style: remote.style,
      slug: remote.slug,
      prompt: remote.prompt ?? existing?.prompt ?? "",
      ops: remote.ops.length ? remote.ops : (existing?.ops ?? []),
      model: remote.model,
      updatedAt: remote.updatedAt,
      versionId: remote.versionId ?? undefined,
      contentHash: remote.contentHash ?? undefined,
      vitalVersion: remote.vitalVersion ?? undefined,
      generationId: remote.generationId ?? (sameVersion ? existing?.generationId : undefined),
      jobId: sameVersion ? (remote.jobId ?? existing?.jobId) : (remote.jobId ?? undefined),
      previewBlob: sameVersion ? existing?.previewBlob : undefined,
      previewContentHash: sameVersion ? existing?.previewContentHash : undefined,
    };
    await saveLocalPreset(record);
  }

  for (const local of await listLocalPresets()) {
    if (!remoteIds.has(local.id)) {
      await deleteLocalPreset(local.id);
    }
  }

  return listLocalPresets();
}
